import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";

import useLanguage from "../../../utils/useLanguage";
import useRequest from "../../../utils/useRequest";
import { toArabicWord } from "number-to-arabic-words/dist/index-node.js";
import { toWords } from "number-to-words";
import { sanitizeNumber } from "../../../utils/sanitizeNumber";

import saveIcon from "../../../assets/save_icon.png";

import CustomButton from "../../../components/CustomButton";
import CustomInput from "../../../components/CustomInput";
import classes from "../../../components/Modal.module.css";
import HotCustomInput from "../../../components/HotCustomInput";
import { DataContext } from "../../../utils/DataContext";
import { UserAuth } from "../../../utils/AuthContext";
import Dropdown from "../../../components/Dropdown";
import ListedTable from "../../../components/table-component/ListedTable";
import DiscountSection from "../../../components/DiscountSection";

// RECEIPT TO PRINT
import { useReactToPrint } from "react-to-print";
import ReceiptTemplate from "../../cashier-system/ReusableCashierReceipt";
import CustomInputWithUnit from "../../../components/CustomInputWithUnit";
import Image from "../../../components/Image";
import Loader from "../../../components/Loader";
import PageWrapper from "../../../components/PageWrapper";

const ManualCustomersCashier = ({ data }) => {
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);
  const { currentDirection } = useLanguage();

  // *** STATES ***
  // Add error fields state for validation
  const [errorFields, setErrorFields] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const spanStyles = currentDirection === "rtl" ? classes.shortSpan : null;

  const receiptRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: receiptRef,
    pageStyle: `
      @page {
        size: 80mm 100mm; 
        margin: 5mm;
      }
      @media print {
        body {
          width: 70mm auto; 
          margin: 0;
          padding: 0;
          font-size: 10pt;
          line-height: 1.2;
        }
      }
    `,
    onAfterPrint: () => console.log("Printed receipt successfully"),
    removeAfterPrint: true,
  });

  // UI related states
  const [loadingProductName, setLoadingProductName] = useState(false);
  const [loadingInvoiceNumber, setLoadingInvoiceNumber] = useState(false);
  const [tableData, setTableData] = useState([]);

  // Invoice related states
  // const [selectedCurrency, setSelectedCurrency] = useState("IQD");
  const [sumOfTotalAmount, setSumOfTotalAmount] = useState(0);
  const [selectedInvoiceType, setSelectedInvoiceType] = useState({
    title: t("cash"),
    value: "cash",
  });
  const [invoice, setInvoice] = useState({
    id: "",
    date: new Date(),
    discount: 0,
    receivedAmount: 0,
    leftAmount: 0,
    notes: "",
    documentPath: "",
    address: "",
    items: [],
  });

  // Price related states
  const [originalPrice, setOriginalPrice] = useState(null);
  const [price, setPrice] = useState(null);

  // Customer related states
  const [selectedCustomer, setSelectedCustomer] = useState({
    id: "",
    name: "",
    phoneNumber: "",
    address: "",
  });

  const currencies = [
    { title: "iqd", value: "IQD" },
    { title: "usd", value: "USD" },
  ];

  const [selectedCurrency, setSelectedCurrency] = useState({
    title: "iqd",
    value: "IQD",
  });

  // *** TABLE CONFIGURATION ***
  const columns = [
    { key: "barcode", title: "code", keepOnMobile: true },
    { key: "name", title: "itemName", keepOnMobile: true },
    { key: "quantity", title: "quantity", keepOnMobile: false },
    { key: "price", title: "price", keepOnMobile: true },
    { key: "totalAmount", title: "totalAmount", keepOnMobile: true },
  ];

  const finalAmountInWords =
    currentDirection === "rtl" ? toArabicWord(price || 0) : toWords(price || 0);

  // *** ENDPOINT FUNCTIONS ***
  // Add validateFields function to check all required fields
  const validateFields = () => {
    setIsSubmitting(true);
    let errors = {};
    let hasError = false;

    // Validate invoice information
    if (!invoice.date) {
      errors["date"] = t("validDateRequired");
      hasError = true;
    }

    // Validate table data
    if (tableData.length === 0) {
      errors["tableData"] = t("atLeastOneItemRequired");
      hasError = true;
    } else {
      const invalidItems = tableData.filter(
        (item) =>
          !item.name || !item.quantity || !item.price || !item.totalAmount
      );
      if (invalidItems.length > 0) {
        errors["tableItems"] = t("allItemDetailsMustBeComplete");
        hasError = true;
      }
    }

    setErrorFields(errors);
    return !hasError;
  };

  // Fetch product name by barcode
  const fetchProductName = async (barcode, rowIndex, currentRows, setRows) => {
    try {
      setLoadingProductName(true);

      const response = await axios.post(
        `${process.env.REACT_APP_URL}/sellInvoice/getProductName`,
        { barcode },
        {
          headers: {
            Authorization: `bearer ${userData?.token}`,
            "Organization-Id": userData?.organizationId,
          },
        }
      );

      if (response.data && response.data.name) {
        // Update only the item name in the row
        const updatedRows = [...currentRows];
        updatedRows[rowIndex] = {
          ...updatedRows[rowIndex],
          name: response?.data?.name,
          unitOfMeasure: response?.data?.unitOfMeasure,
        };

        setRows(updatedRows);

        // Update the invoice items
        setInvoice((prev) => ({
          ...prev,
          items: updatedRows.filter((row) => row.code && row.item),
        }));
      }
    } catch (error) {
      console.error("Failed to fetch product name:", error);
    } finally {
      setLoadingProductName(false);
    }
  };

  // Generate new invoice number
  const getNewInvoiceNumber = async () => {
    try {
      setLoadingInvoiceNumber(true);
      const newInvoiceNumber = await axios.get(
        `${process.env.REACT_APP_URL}/sellInvoice`,
        {
          params: {
            organizationId: userData?.organizationId,
          },
        }
      );
      setInvoice((prev) => ({
        ...prev,
        id: newInvoiceNumber?.data?.invoiceNumber,
        documentPath: newInvoiceNumber?.data?.document?.[0]?.path,
      }));
    } catch (error) {
      console.error("Failed to get a new invoice number");
    } finally {
      setLoadingInvoiceNumber(false);
    }
  };

  // Create invoice
  const handleCreateInvoice = async () => {
    // Validate fields before submission
    if (!validateFields()) {
      console.error("Validation failed", errorFields);
      return;
    }

    try {
      setIsLoading(true);

      const payload = {
        // customer
        id: invoice.id,
        customerName: selectedCustomer.name,
        customerPhoneNumber: selectedCustomer.phoneNumber,
        customerAddress: selectedCustomer.address,

        // invoice filed
        date: invoice.date,
        notes: invoice.notes,
        totalPrice: originalPrice || 0,
        finalAmount: price || 0,
        receivedAmount:
          parseFloat(sanitizeNumber(invoice?.receivedAmount)) || 0,
        leftAmount: parseFloat(sanitizeNumber(invoice?.leftAmount)) || 0,
        discount: parseFloat(sanitizeNumber(originalPrice - price)) || 0,
        finalAmountInAr: toArabicWord(price || ""),
        finalAmountInEn: toWords(price || ""),
        products: tableData,
        invoiceType: selectedInvoiceType.value,

        // ids
        userId: parseInt(userData?.userId),
        organizationId: parseInt(userData?.organizationId),
        customerId: selectedCustomer?.id || null,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_URL}/sellInvoice`,
        payload,
        {
          headers: {
            Authorization: `bearer ${userData?.token}`,
          },
        }
      );

      if (response.status === 201) {
        setTableData([]);
        setSelectedCustomer({
          id: "",
          name: "",
          phoneNumber: "",
          address: "",
        });
        setInvoice({
          id: "",
          date: new Date(),
          discount: 0,
          receivedAmount: 0,
          leftAmount: 0,
          notes: "",
          documentPath: "",
          address: "",
          items: [],
        });
        // Clear error fields
        setErrorFields({});
        setIsSubmitting(false);
        getNewInvoiceNumber();
        handlePrint();
      }
    } catch (error) {
      console.error("Failed to create invoice:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // *** EVENT HANDLERS ***
  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setInvoice((prev) => ({ ...prev, [name]: value }));

    // Also update selectedCustomer if it's a customer field
    if (name === "phoneNumber" || name === "address") {
      setSelectedCustomer((prev) => ({ ...prev, [name]: value }));
    }

    if (errorFields[name]) {
      setErrorFields((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSaveAndPrint = async () => {
    await handleCreateInvoice();
  };

  const handleSumTotalAmount = () => {
    const totalSum = tableData.reduce(
      (sum, item) => sum + parseInt(item.totalAmount),
      0
    );
    setSumOfTotalAmount(totalSum);
  };

  // *** EFFECTS ***
  useEffect(() => {
    const totalSum = tableData.reduce((sum, item) => {
      return sum + (parseFloat(item.totalAmount) || 0);
    }, 0);
    setSumOfTotalAmount(totalSum);
    setOriginalPrice(totalSum);
    setPrice(totalSum);

    // Clear table-related errors when table data changes
    if (tableData.length > 0 && errorFields.tableData) {
      setErrorFields((prev) => ({
        ...prev,
        tableData: undefined,
      }));
    }
  }, [tableData]);

  // Initialize on component mount
  useEffect(() => {
    getNewInvoiceNumber();
  }, []);

  // Update prices when table data changes
  useEffect(() => {
    const newPrice = tableData.reduce((sum, item) => {
      const price = parseInt(item.price);
      return isNaN(price) ? sum : sum + price;
    }, 0);

    setOriginalPrice(newPrice);
    setPrice(newPrice);
  }, [tableData]);

  // Replace your current useEffect with this:
  useEffect(() => {
    const totalSum = tableData.reduce((sum, item) => {
      return sum + (parseFloat(item.totalAmount) || 0);
    }, 0);
    setSumOfTotalAmount(totalSum);
    setOriginalPrice(totalSum);
    setPrice(totalSum);
  }, [tableData]);

  // Initialize table data if items exist in data
  useEffect(() => {
    if (data?.items && data.items.length > 0) {
      setTableData(data.items);
    }
  }, [data?.items]);

  return (
    <PageWrapper>
      <div className="o-column u-padding--bottom_500">
        {/* <img src={invoice?.documentPath} alt="test" /> */}
        <h2>🛒 {t("manualCustomersCashier")}</h2>

        {/* Body section 1 */}
        <section className="o-row o-row--horizontal-align_space-between">
          {/* Column 1 */}
          <div className="o-row o-row--gap-size_300">
            <div className="o-column o-column--gap-size_500 u-padding--top_100">
              <span>{t("invoiceNumber")}: </span>
              <span>{t("Date")}: </span>
            </div>

            <div className="o-column o-column--gap-size_300 o-column--horizontal-align_start">
              <div className="u-width_fullwidth">
                <CustomInput
                  value={invoice?.id}
                  loading={loadingInvoiceNumber}
                />
              </div>

              <div className="u-width_fullwidth">
                <CustomInput
                  type="date"
                  name="date"
                  value={invoice?.date}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>
          {/* Column 2 */}
          <div className="o-row o-row--gap-size_300 o-row--vertical-align_center">
            <div className="o-column o-column--gap-size_500 u-padding--top_100">
              <span className={spanStyles}>{t("invoiceType")}: </span>
              <span className={spanStyles}>{t("currency")}: </span>
            </div>
            <div className="o-column o-row--gap-size_300">
              <div className="u-width_fullwidth">
                <Dropdown
                  options={[
                    { title: t("cash"), value: "cash" },
                    { title: t("debt"), value: "debt" },
                  ]}
                  selected={selectedInvoiceType}
                  setSelected={setSelectedInvoiceType}
                  flex
                />
              </div>
              <div className="u-width_fullwidth">
                <Dropdown
                  options={currencies}
                  selected={selectedCurrency}
                  setSelected={setSelectedCurrency}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Body section 2 */}
        <section className="u-margin--top_500 u-margin--bottom_500">
          <ListedTable
            columns={columns}
            initialData={tableData}
            handleAddRow={(newItems) => {
              setTableData(newItems);
            }}
            onFetchProductName={fetchProductName}
            loadingProductName={loadingProductName}
            handleSelectCurrency={setSelectedCurrency}
            selectedCurrency={selectedCurrency}
          />
          {errorFields["tableData"] && (
            <div className="o-row u-margin--top_200" style={{ color: "red" }}>
              {errorFields["tableData"]}
            </div>
          )}
          {errorFields["tableItems"] && (
            <div className="o-row u-margin--top_100" style={{ color: "red" }}>
              {errorFields["tableItems"]}
            </div>
          )}
        </section>

        {/* Body section 3 */}

        <section className="u-margin--bottom_200 o-column o-column--gap-size_400 u-max-width_500">
          <section className={classes.section}>
            <DiscountSection
              originalPrice={sumOfTotalAmount}
              onPriceChange={(priceValue) => setPrice(priceValue)}
              inputMaxWidth={true}
              selectedCurrency={selectedCurrency?.value}
            />
          </section>
          <div>
            <span>{t("received")}: </span>
            <CustomInputWithUnit
              type="number"
              name="receivedAmount"
              onChange={(e) => {
                handleInputChange(e);
                setInvoice((prev) => {
                  return {
                    ...prev,
                    leftAmount: price - e.target.value,
                  };
                });
              }}
              value={invoice.receivedAmount}
              unit={selectedCurrency?.value}
            />
          </div>
          <div>
            <span>{t("left")}: </span>
            <CustomInputWithUnit
              name="leftAmount"
              readonly={true}
              onChange={handleInputChange}
              value={
                price - invoice?.receivedAmount > 0
                  ? (price - invoice?.receivedAmount).toFixed(2)
                  : 0
              }
              unit={selectedCurrency?.value}
            />
          </div>

          <section>
            <span>{t("notes")}: </span>
            <CustomInput
              type="textarea"
              name="notes"
              onChange={handleInputChange}
              value={invoice?.notes || ""}
            />
          </section>
        </section>

        {/* Loader */}
        {isLoading ? (
          <>
            <Loader />
          </>
        ) : null}

        {/* Body section 5 -> Actions */}
        <section className="o-row o-row--horizontal-align_end o-row--gap-size_500 u-margin--top_200">
          <CustomButton
            title={t("saveAndPrint")}
            onClick={handleSaveAndPrint}
            icon={saveIcon}
            iconSize={16}
          />
        </section>

        {/* RECEIPT TEMPLATE FOR PRINTING */}
        <ReceiptTemplate
          ref={receiptRef}
          title="فاتورة"
          headerImage={invoice?.documentPath}
          headerText=" "
          invoiceNumber={invoice?.id}
          invoiceDate={
            invoice?.date
              ? new Date(invoice.date).toLocaleDateString("en-UK")
              : ""
          }
          receivedAmount={invoice.receivedAmount}
          leftAmount={
            price - invoice?.receivedAmount > 0
              ? (price - invoice?.receivedAmount).toFixed(2)
              : 0
          }
          userData={userData}
          items={tableData.map((item) => ({
            name: item.name,
            quantity: item.quantity,
            price: item.price,
          }))}
          subtotal={sumOfTotalAmount}
          discount={(sumOfTotalAmount - price).toFixed(2)}
          total={price}
          showThankYou={true}
          showArabicTotal={true}
          time={new Date()}
        />
      </div>
    </PageWrapper>
  );
};

export default ManualCustomersCashier;
